/**
 * Message Component
 * Displays a single chat message with metadata, reactions, and actions
 */

import React, { useState } from 'react';
import { Avatar, Dropdown, Button, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import {
  UserOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  SmileOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/context/AuthContext';
import { useUpdateMessage, useArchiveMessage } from '@/hooks/useChat';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { MessageReactions } from './MessageReactions';
import { MessageStatus } from './MessageStatus';
import type { Message as MessageType } from '@/api/types/chat.types';
import './Message.css';

interface MessageProps {
  /** Message data */
  message: MessageType;
  /** Is this message grouped with previous message from same author */
  isGrouped?: boolean;
  /** Is this the last message in the group */
  isLastInGroup?: boolean;
  /** Callback to retry failed message */
  onRetry?: () => void;
}

export const Message: React.FC<MessageProps> = ({
  message,
  isGrouped = false,
  isLastInGroup = true,
  onRetry,
}) => {
  const { user } = useAuth();
  const { formatTime } = useUserTimezone();
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(message.message);

  const updateMessageMutation = useUpdateMessage();
  const archiveMessageMutation = useArchiveMessage();

  // Check if this is the current user's message
  const isOwnMessage = message.created_by === user?.id;

  // Handle edit message
  const handleEdit = () => {
    setIsEditing(true);
    setEditedText(message.message);
  };

  // Handle save edited message
  const handleSaveEdit = async () => {
    if (!editedText.trim() || editedText === message.message) {
      setIsEditing(false);
      return;
    }

    try {
      await updateMessageMutation.mutateAsync({
        messageId: message.id,
        data: { message: editedText.trim() },
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update message:', error);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedText(message.message);
  };

  // Handle archive message
  const handleArchive = async () => {
    try {
      await archiveMessageMutation.mutateAsync({
        messageId: message.id,
        chatId: message.chat_id,
      });
    } catch (error) {
      console.error('Failed to archive message:', error);
    }
  };

  // Handle copy message to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.message);
      // Optional: Show success message
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  // Dropdown menu items
  const menuItems: MenuProps['items'] = isOwnMessage
    ? [
        {
          key: 'copy',
          icon: <CopyOutlined />,
          label: 'Copy',
          onClick: handleCopy,
        },
        {
          key: 'edit',
          icon: <EditOutlined />,
          label: 'Edit',
          onClick: handleEdit,
        },
        {
          key: 'delete',
          icon: <DeleteOutlined />,
          label: 'Delete',
          danger: true,
          onClick: handleArchive,
        },
      ]
    : [
        {
          key: 'copy',
          icon: <CopyOutlined />,
          label: 'Copy',
          onClick: handleCopy,
        },
      ];

  return (
    <div
      className={`message ${isOwnMessage ? 'message-own' : 'message-other'} ${
        isGrouped ? 'message-grouped' : ''
      } ${isLastInGroup ? 'message-last-in-group' : ''}`}
    >
      {/* Avatar (only show for first message in group) */}
      {!isGrouped && (
        <div className="message-avatar">
          <Avatar
            size="small"
            src={message.created_by_user?.avatar_url}
            icon={!message.created_by_user?.avatar_url && <UserOutlined />}
          >
            {!message.created_by_user?.avatar_url &&
              (message.created_by_user?.display_name?.charAt(0).toUpperCase() ||
                'U')}
          </Avatar>
        </div>
      )}

      {/* Message content */}
      <div className="message-content-wrapper">
        {/* Author name and timestamp (only for first message in group) */}
        {!isGrouped && (
          <div className="message-header">
            <span className="message-author">
              {message.created_by_user?.display_name || 'Unknown User'}
            </span>
            <span className="message-time">
              {formatTime(message.created_at, 'HH:mm')}
              {/* Show status indicator only for own messages */}
              {isOwnMessage && (
                <MessageStatus status={message.status} onRetry={onRetry} />
              )}
            </span>
          </div>
        )}

        {/* Message bubble */}
        <div className="message-bubble">
          {isEditing ? (
            // Edit mode
            <div className="message-edit-mode">
              <textarea
                className="message-edit-textarea"
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSaveEdit();
                  }
                  if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
              />
              <div className="message-edit-actions">
                <Button size="small" onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  type="primary"
                  onClick={handleSaveEdit}
                  loading={updateMessageMutation.isPending}
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            // View mode
            <>
              <div className="message-text">
                {message.message}
                {message.is_edited && (
                  <span className="message-edited-indicator"> (edited)</span>
                )}
              </div>

              {/* Attachments (future phase) */}
              {message.has_attachments && message.attachments_metadata && (
                <div className="message-attachments">
                  {message.attachments_metadata.files.map((file) => (
                    <div key={file.id} className="message-attachment">
                      📎 {file.filename}
                    </div>
                  ))}
                </div>
              )}

              {/* Reactions */}
              <MessageReactions messageId={message.id} showAddButton={true} />

              {/* Actions (hover menu) */}
              {isOwnMessage && (
                <div className="message-actions">
                  <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                    <Button
                      type="text"
                      size="small"
                      icon={<MoreOutlined />}
                      className="message-actions-button"
                    />
                  </Dropdown>
                </div>
              )}
            </>
          )}
        </div>

        {/* Timestamp for grouped messages (show on hover) */}
        {isGrouped && (
          <Tooltip title={formatTime(message.created_at, 'HH:mm')}>
            <span className="message-time-grouped" />
          </Tooltip>
        )}
      </div>
    </div>
  );
};
